import React, { FC, useCallback, useContext, useEffect, useMemo } from 'react';
import { createServerSidePaginatedFetcher, IServerSidePaginatedFetcherParams } from '../../contexts/Fetcher';
import { useHistory, useParams } from 'react-router-dom';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns, GridRowParams } from '@mui/x-data-grid';
import { getDateValueFormatter } from '../../valueFormatters/DateValueFormatter';
import { DateFormat } from '../../helpers/dateFormat';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { PendingTaskDetailsDialog } from './PendingTaskDetailsDialog';
import { createStatusChipComponent } from '../StatusChip/StatusChip';
import { routeWithQuery } from '../../helpers/routeWithQuery';
import { useTranslation } from 'react-i18next';
import { apiScheduled } from '../../resources/api/apiScheduled';
import { AuthenticatedRoutes } from '../../routers/AuthenticatedRoutes';
import { LazyUser } from '../LazyData/LazyUser';
import { useQueryParam } from '../../hooks/useQueryParam';
import { useCloseDialog } from '../../hooks/useCloseDialog';
import { PendingJobType } from '../../models/CronJob';

interface IUrlParams {
  job_type?: PendingJobType;
}

const { useGetAllJobs } = apiScheduled;

export const PendingTasksFetcher = createServerSidePaginatedFetcher(() => {
  const { execute: getScheduledJobs } = useGetAllJobs();
  const { job_type: jobType } = useParams<IUrlParams>();

  return useCallback(
    (params: IServerSidePaginatedFetcherParams) => {
      if (!jobType) return Promise.resolve(null);
      return getScheduledJobs({
        ...params,
        filter: {
          filterOp: 'eq',
          filterField: 'operation',
          filterValue: jobType,
        },
      });
    },
    [getScheduledJobs, jobType]
  );
});

const jobTypes: PendingJobType[] = [
  'customerNewProduct',
  'deactivateOfflineLicense',
  'customerRenewProduct',
  'licenseExpiringSoon',
  'customerSuspendProduct',
];

const StatusChip = createStatusChipComponent({
  done: 'lime',
  ignored: 'lime',
  failed: 'red',
  inProgress: 'orange',
  todo: 'default',
});

const defaultSortQueryParams = {
  sortBy: 'created',
  sortOrder: 'desc',
};

const PendingTasksContent: FC = () => {
  const { data } = useContext(PendingTasksFetcher.Context);
  const { job_type: jobType } = useParams<IUrlParams>();

  const jobId = parseInt(useQueryParam('job') ?? '');

  const history = useHistory();
  const { t } = useTranslation();

  useEffect(() => {
    if (!jobType)
      history.replace(routeWithQuery(AuthenticatedRoutes.JobsType, ['customerNewProduct'], defaultSortQueryParams));
  }, [jobType]);

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'ownerUuid',
        headerName: t('pendingTasks.table.ownerHeader'),
        width: 120,
        renderCell: (params) => {
          if (!params.value) {
            return null;
          }
          return <LazyUser id={params.value} />;
        },
        filterable: false,
        sortable: false,
      },
      {
        field: 'request',
        headerName: t('pendingTasks.table.requestHeader'),
        flex: 1,
        valueFormatter: (params) => JSON.stringify(params.value),
        filterable: false,
        sortable: false,
      },
      {
        field: 'scheduleTime',
        headerName: t('pendingTasks.table.scheduleTimeHeader'),
        width: 180,
        valueFormatter: getDateValueFormatter(DateFormat.DateWithFullTime),
        filterable: false,
      },
      {
        field: 'status',
        headerName: t('common.tableHeader.status'),
        filterable: false,
        width: 120,
        renderCell: StatusChip,
      },
      {
        field: 'created',
        headerName: t('common.tableHeader.created'),
        filterable: false,
        width: 130,
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
      },
      {
        field: 'updated',
        headerName: t('common.tableHeader.updated'),
        filterable: false,
        width: 130,
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
      },
    ],
    []
  );

  const onTabChange = useCallback((event: unknown, jobType: PendingJobType) => {
    history.push(routeWithQuery(AuthenticatedRoutes.JobsType, [jobType], defaultSortQueryParams));
  }, []);

  const chosenJob = useMemo(() => {
    if (!data || !jobId) return void 0;
    return data.cronJobs.data.find((job) => job.id === jobId);
  }, [data, jobId]);

  const getRowLink = useCallback(
    (params: GridRowParams) => {
      const searchParams = new URLSearchParams(history.location.search);
      searchParams.set('job', params.row.id);
      return `?${searchParams.toString()}`;
    },
    [jobType, history.location.search]
  );

  const closeDialog = useCloseDialog(void 0, ['job']);

  if (!data) return null;
  return (
    <div>
      <Tabs
        value={jobType}
        indicatorColor="primary"
        textColor="primary"
        onChange={onTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {jobTypes.map((jobType) => {
          return (
            <Tab key={jobType} label={jobType.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()} value={jobType} />
          );
        })}
      </Tabs>

      <UrlQueryDataGrid
        columns={columns}
        rows={data.cronJobs.data}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        paginationMode={'server'}
        sortingMode={'server'}
        filterMode={'server'}
        getRowLink={getRowLink}
        rowCount={data.cronJobs.page.data.total}
      />

      <PendingTaskDetailsDialog closeDialog={closeDialog} pendingTask={chosenJob} />
    </div>
  );
};

export const PendingTasks: FC = () => {
  return (
    <PendingTasksFetcher.WAF>
      <PendingTasksContent />
    </PendingTasksFetcher.WAF>
  );
};
